/**
 * @module core/aem
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable: no-loop-func */
/*tslint:disable:max-file-line-count */
import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { ProductDetails } from '@experience/shop/product-details/interfaces/product-details.interface';
import {
  ColorOption,
  MemoryOption,
  ItemDetail,
  Promotion,
} from '@support/api/common/product.interface';
import { PRODUCT_OFFERINGS_CONSTANTS } from '../product-offerings/product-offerings.constants';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { SelectItem } from 'primeng/api';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { ProductDetailsService } from '@experience/shop/services/product-details.service';
import { SETTINGS } from '@environment/settings';
import { ProductDetailsDefaultModel } from '@experience/shop/shared-browse/models/productDetails-default.model';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'tmo-product-offerings-accessories',
  templateUrl: './product-offerings-accessories.component.html',
  styleUrls: ['../../../../../style-sheets/components/dropdown.component.scss'],
})
export class ProductOfferingsAccessoriesComponent implements OnInit, OnDestroy {
  /**
   * Stores device details coming from API call
   */
  public deviceDetails: ProductDetails;
  /**
   * Stores the estimated To delivery date in desired format
   */
  public toDate: string;
  /**
   * Stores the estimated From delivery date in desired format
   */
  public fromDate: string;
  /**
   * Stores the estimated date range message to be displayed on page
   */
  public fromDateToDate: string;

  /**
   * Flag to check whether to display Add to cart message or not
   */
  public showAddToCartMsg: boolean;
  /**
   * Stores the content coming from AEM call
   */
  public pdpStaticContent: ConfigData;
  /**
   * Stores the Quantity drop down options
   */
  public qtyList: SelectItem[];
  /**
   * to distroy the event OnDestroy life cycle
   */
  public subscription: Subscription;
  /**
   * Stores Product Availability Types constant to be used in Html
   */
  public productAvailability: {};
  /**
   * to store the max quantity in cart
   */
  public maxQtyAllowed: number;
  /**
   * to store the form for accessory
   */
  public accessoriesOfferingsFormGroup: UntypedFormGroup;
  /**
   * count of the accessories already added to cart
   */
  public existingAccessoryCount: number;
  /**
   * Holds value for selected quantity
   */
  public selectedQuantity: number;
  /**
   * Holds count for accessory total quantity
   */
  public accessoriesTotalQuantity: number;
  /**
   * Flag for Assisted channel i.e. Retail & Care
   */
  // public isAssistedFlow: boolean;
  /**
   * Stores model data
   */
  public product: ProductDetailsDefaultModel;
  /**
   * Holds the external domain URL to load images
   */
  public extTmoImagesUrl: string;
  /**
   * Holds color options for an accessory
   */
  public colorOptions: ColorOption[];
  /**
   * Holds the value of promotions array
   */
  public promotions: Promotion[];
  /**
   * Holds all available memory options
   */
  public memoryOptions: MemoryOption[];
  /**
   * Holds all item details for the current offerId type (to be used to populate the selectedItem)
   */
  public itemDetails: ItemDetail[];
  /**
   * Holds the selected item to bind to the screen
   */
  public selectedItem: ItemDetail;
  /**
   * Stores value of saved amount
   */
  public savedAmountText: string;
  /**
   * Stores the value of list price whole Number
   */
  public listPriceWholeNumber: string;
  /**
   * Stores the value of list price decimal Number
   */
  public listPriceDecimalNumber: string;
  /**
   * Modal related to close accounts
   */
  public modal: DynamicModal;
  /**
   * Flag to see open or close modal
   */
  public showPromoModal: boolean;
  /**
   * Stores value of promo description
   */
  public promoDescription: Promotion;
  /**
   * to unregister subscription
   */
  private ngUnsubscribe: Subject<void>;

  constructor(
    private commonUtilService: CommonUtilService,
    private objectService: ObjectService,
    private formBuilder: UntypedFormBuilder,
    private productDetailsService: ProductDetailsService,
    private stateUtilService: StateStoreUtilsService
  ) {
    this.showPromoModal = false;
    this.showAddToCartMsg = false;
    this.extTmoImagesUrl = SETTINGS.EXT_IMAGE_URL;
    this.qtyList = [];
    this.maxQtyAllowed = PRODUCT_OFFERINGS_CONSTANTS.MAX_QTY;
    this.ngUnsubscribe = new Subject<void>();
    this.modal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'product-offerings-accessories-modal',
    };
  }

  /**
   * loads the initial state of product offerings accessories component
   * @returns void
   */
  public ngOnInit(): void {
    this.accessoriesOfferingsFormGroup = this.formBuilder.group({
      qty: [],
    });
    this.accessoriesTotalQuantity = PRODUCT_OFFERINGS_CONSTANTS.ONE;
    this.productAvailability = PRODUCT_OFFERINGS_CONSTANTS;
    this.loadProductDetails();
    // this.isAssistedFlow = (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL ||
    //   SETTINGS.CHANNEL.toLowerCase() === CHANNELS.CARE);
  }

  /**
   * loads the initial data of product offerings accessories component
   * @returns void
   */
  public loadProductDetails(): void {
    this.productDetailsService
      .getProductDetailsContent()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((content: ConfigData) => {
        this.pdpStaticContent = content;
        const offerId = this.stateUtilService.getSelectedOfferId();
        if (offerId) {
          this.product = this.stateUtilService.getSelectedDeviceDetails(offerId);
          this.deviceDetails = this.product.getProductDetails();
          this.itemDetails = this.product.getItemDetails();
          this.memoryOptions = this.product.getMemoryOptions();
          this.colorOptions = this.product.getColorOptions();
          this.selectedItem = this.selectedItem
            ? this.selectedItem
            : this.product.setSelectedColorVarient(this.objectService);
          const discount = (this.selectedItem.listPrice - this.selectedItem.salePrice).toString();
          this.savedAmountText = this.commonUtilService.messageFormatter(
            this.pdpStaticContent.savedAmount,
            ['$' + discount]
          );
        }
        this.splitPrice();
        this.setDefaultValues();
      });
  }

  /**
   * Function to split amount
   */
  public splitPrice(): void {
    const numberOfDecimalDigits = 2;
    const listAmount = this.objectService.get(this.selectedItem, 'listPrice');
    if (listAmount) {
      const tempNumber = listAmount.toFixed(numberOfDecimalDigits);
      this.listPriceWholeNumber = tempNumber.split('.')[0];
      this.listPriceDecimalNumber = tempNumber.split('.')[1];
    } else {
      this.listPriceWholeNumber = this.listPriceDecimalNumber = '00';
    }
  }

  /**
   * sets the defaults values of product offerings accessories component
   * @returns void
   */
  public setDefaultValues(): void {
    this.existingAccessoryCount = PRODUCT_OFFERINGS_CONSTANTS.MAX_QTY;
    this.createQtyDropdown();
  }

  /**
   * parse Selected memory Option
   */
  public getMemoryOption(memoryOption: string): string {
    const memoryNameArray = memoryOption.split(' ');
    return memoryNameArray[0].concat(memoryNameArray[1]);
  }

  /**
   * update the selected color of device
   * @param color passes the selected color
   * @returns void
   */
  public updateSelectedColor(color: string): void {
    this.selectedItem = this.product.setSelectedColorVarient(this.objectService, color);
  }

  /**
   * Component lifecycle hook to destroy the component
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * adds the accessory to cart or updates the quanity if the accessory already exists
   * @param sku sku of the accessory to be added or updated in cart
   * @returns void
   */
  public addAccessoriesToCart(isCheckout: boolean): void {
    this.showAddToCartMsg = !isCheckout;
  }

  /**
   * Updates selected accessory quantity
   * @returns void
   */
  public onUpdateQty(): void {
    this.accessoriesTotalQuantity = this.selectedQuantity;
  }

  /**
   * Method to handle Click event
   * @returns void
   */
  public closeModal(): void {
    this.modal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
    this.showPromoModal = false;
  }

  /**
   * Showing of modal on toggle event
   * @param event : toggle event
   * @returns void
   */
  public showPromotionalModal(index?: number): void {
    this.promoDescription = this.selectedItem.itemPromotions[index];
    this.showPromoModal = true;
    this.modal.toggleModalEmitter.emit({ open: true, close: false });
  }

  /**
   * creates a form for quantity dropdown and assigns a default selected quantity
   * @returns void
   */
  private createQtyDropdown(): void {
    for (let key = 1; key <= this.maxQtyAllowed; key++) {
      const dropdownData = { label: key.toString(), value: key };
      this.qtyList.push(dropdownData);
    }
    const selectedQty = this.existingAccessoryCount
      ? this.existingAccessoryCount
      : this.qtyList[0].value;
    this.accessoriesOfferingsFormGroup = this.formBuilder.group({
      qty: [selectedQty],
    });
  }
}
